import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import './VideoSettings.css';
import { Select, MenuItem, SelectChangeEvent } from "@mui/material";
import ColorPicker from './ColorPicker';

// Render format types - video formats call /new-render, image formats call /export-png
export type VideoFormat = 
  | 'H264' | 'H265' 
  | 'ProRes_422_Proxy' | 'ProRes_422_LT' | 'ProRes_422' | 'ProRes_422_HQ' 
  | 'ProRes_4444' | 'ProRes_4444_XQ';

export type ImageFormat = 'PDF' | 'PNG' | 'TIFF';

export type RenderFormat = VideoFormat | ImageFormat;

export const VIDEO_FORMATS: VideoFormat[] = [
  'H264', 'H265', 
  'ProRes_422_Proxy', 'ProRes_422_LT', 'ProRes_422', 'ProRes_422_HQ', 
  'ProRes_4444', 'ProRes_4444_XQ'
];

export const IMAGE_FORMATS: ImageFormat[] = ['PDF', 'PNG', 'TIFF'];

export function isVideoFormat(format: RenderFormat): format is VideoFormat {
  return VIDEO_FORMATS.includes(format as VideoFormat);
}

export function isImageFormat(format: RenderFormat): format is ImageFormat {
  return IMAGE_FORMATS.includes(format as ImageFormat);
}

export type VideoMode = "roller" | "cards";
export type SecondsPerCard = number | number[] | null;

export interface VideoSettings {
  background_color: string;
  fps_num: number;
  resolution: string;
  transparent: boolean;
  video_time: number;
  aspect_ratio: string;
  video_format: RenderFormat;
  fonts?: Record<string, string>;
  logos?: Record<string, { url: string; isEnabled: boolean }>;
  mode: "roller" | "cards";
  seconds_per_card: SecondsPerCard;
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
  background_color: '#000000',
  fps_num: 30,
  resolution: "HD",
  transparent: false,
  video_time: 60,
  aspect_ratio: '16:9',
  video_format: 'H264',
  fonts: {},
  logos: {},
  mode: "roller",
  seconds_per_card: null,
};

export const RESOLUTION_WIDTHS: Record<string, number> = {
  "HD": 1280,
  "2K": 2048,
  "UHD": 3840,
  "4K": 4096,
};

export function calculateVideoHeight(settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio'>): number {
  const width: number = RESOLUTION_WIDTHS[settings.resolution] ?? 1920;
  const [w, h] = settings.aspect_ratio.split(":").map(Number);
  if (w && h) return Math.round(width * (h / w));
  return 1080;
}

export function calculatePixelsPerFrame(
  settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio' | 'video_time' | 'fps_num'>
): number | null {
  const { video_time, fps_num } = settings;
  if (!video_time || video_time <= 0 || !fps_num || fps_num <= 0) return null;
  const height: number = calculateVideoHeight(settings);
  return Number((height / (video_time * fps_num)).toFixed(2));
}

type VideoTimeMode = 'total' | 'pixels';
const DEFAULT_VIDEO_TIME_MODE: VideoTimeMode = 'pixels';
function displaySecondsPerCard(value: SecondsPerCard): string {
  if (value === null) return "";
  if (typeof value === 'number') return value.toString();
  return JSON.stringify(value);
}

function parseSecondsPerCard(raw: string): SecondsPerCard {
  const trimmed: string = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0) return parsed;
    if (Array.isArray(parsed)) {
      const nums: number[] = parsed
        .map((e: unknown) => Number(e))
        .filter((n: number) => Number.isFinite(n) && n > 0);
      return nums.length > 0 ? nums : null;
    }
  } catch { /* not valid JSON */ }
  return null;
}

/** Counts valid numeric entries in a partial or complete array string like "[3, 5," or "[3, 5, 4]". */
function countLiveArrayEntries(raw: string): number | null {
  const trimmed: string = raw.trim();
  if (!trimmed.startsWith('[')) return null;
  const inner: string = trimmed.slice(1).replace(/\].*$/, '');
  const parts: string[] = inner.split(',');
  let count: number = 0;
  for (const part of parts) {
    const n: number = Number(part.trim());
    if (part.trim() !== '' && Number.isFinite(n) && n > 0) count++;
  }
  return count;
}

function pixelsPerFrameToSeconds(
  pixelsPerFrame: number,
  settings: Pick<VideoSettings, 'resolution' | 'aspect_ratio' | 'fps_num'>
): number {
  const height: number = calculateVideoHeight(settings);
  return height > 0 ? Number((height / (pixelsPerFrame * settings.fps_num)).toFixed(2)) : 0;
}

function toDisplayVideoTime(settings: VideoSettings, mode: VideoTimeMode): string {
  if (mode === 'pixels') {
    if (!settings.video_time || settings.video_time <= 0) return "";
    const pxPerFrame: number | null = calculatePixelsPerFrame(settings);
    return pxPerFrame !== null ? pxPerFrame.toString() : "";
  }

  return settings.video_time ? settings.video_time.toString() : "";
}

interface VideoSettingsPanelProps {
  settings: VideoSettings;
  onSettingsChange: (settings: VideoSettings) => void | Promise<void>;
  showTitle?: boolean;
  totalCards?: number;
}

export const VideoSettingsPanel: React.FC<VideoSettingsPanelProps> = ({ 
  settings, 
  onSettingsChange,
  showTitle = true,
  totalCards,
}) => {
  const [timeMode, setTimeMode] = useState<VideoTimeMode>(DEFAULT_VIDEO_TIME_MODE);
  const [videoTimeInput, setVideoTimeInput] = useState(() => 
    toDisplayVideoTime(settings, DEFAULT_VIDEO_TIME_MODE)
  );
  const [cardDelayInput, setCardDelayInput] = useState<string>(() =>
    displaySecondsPerCard(settings.seconds_per_card)
  );
  const isEditingRef = useRef<boolean>(false);
  const isCardDelayEditingRef = useRef<boolean>(false);

  // Sync input with external settings when not actively editing.
  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }
    const nextDisplay: string = toDisplayVideoTime(settings, timeMode);
    setVideoTimeInput(nextDisplay);
  }, [settings, timeMode]);

  useEffect(() => {
    if (isCardDelayEditingRef.current) return;
    setCardDelayInput(displaySecondsPerCard(settings.seconds_per_card));
  }, [settings.seconds_per_card]);

  const handleSettingsChange = (updates: Partial<VideoSettings>) => {
    const newSettings = { ...settings, ...updates };
    const result = onSettingsChange(newSettings);
    if (result instanceof Promise) {
      result.catch(error => {
        console.error('Error in onSettingsChange:', error);
      });
    }
  };

  const handleModeChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ mode: e.target.value as VideoMode });
  }
  const handleFpsChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ fps_num: parseInt(e.target.value, 10) });
  };

  const handleResolutionChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ resolution: e.target.value });
  };

  const handleAspectRatioChange = (e: SelectChangeEvent<string>) => {
    handleSettingsChange({ aspect_ratio: e.target.value });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setVideoTimeInput(raw);

    if (raw === "" || raw === null) {
      handleSettingsChange({ video_time: 0 });
      return;
    }

    const value = parseFloat(raw);
    if (isNaN(value) || value <= 0) {
      handleSettingsChange({ video_time: 0 });
      return;
    }

    if (timeMode === 'pixels') {
      const seconds = pixelsPerFrameToSeconds(value, settings);
      handleSettingsChange({ video_time: seconds });
    } else {
      handleSettingsChange({ video_time: value });
    }
  };

  const handleTransparentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSettingsChange({ transparent: e.target.checked });
  };

  const handleTimeModeChange = (e: SelectChangeEvent<string>) => {
    const newMode = e.target.value as VideoTimeMode;
    setTimeMode(newMode);
    const nextDisplay = toDisplayVideoTime(settings, newMode);
    setVideoTimeInput(nextDisplay);
  };

  const handleCardDelayInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardDelayInput(e.target.value);
  };

  const commitCardDelayInput = () => {
    const parsedValue: SecondsPerCard = parseSecondsPerCard(cardDelayInput);
    handleSettingsChange({ seconds_per_card: parsedValue });
    setCardDelayInput(displaySecondsPerCard(parsedValue));
  };

  return (
    <div className="video-settings-panel">
      {showTitle ? <h2>{t`Video Settings`}</h2> : null}

      <div className="video-settings-group">
        {/* Video mode (roller/card) */}
        <div className="setting-item">
          <label>{t`Mode`}</label>
          <div className="input-wrapper">
            <Select
              value={settings.mode}
              onChange={handleModeChange}
              className="video-setting-select video-setting-select--fps"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="roller">{t`Roller`}</MenuItem>
              <MenuItem value="cards">{t`Cards`}</MenuItem>
            </Select>
          </div>
        </div>

        {settings.mode === "cards" ? (
          <div className="setting-item">
            <label className="setting-label-with-tooltip">
              {t`Seconds Per Card`}
              <div className="tooltip-wrapper">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">{t`A single number (e.g. 3.5) applies to all cards. A JSON array (e.g. [3, 5, 4]) sets per-card durations.`}</span>
              </div>
            </label>
            {(() => {
              const liveCount: number | null = totalCards !== undefined
                ? countLiveArrayEntries(cardDelayInput)
                : null;
              const remaining: number | null = liveCount !== null
                ? totalCards! - liveCount
                : null;
              const hasRemaining: boolean = remaining !== null && remaining > 0;
              return (
                <div className="input-wrapper" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                  <input
                    type="text"
                    value={cardDelayInput}
                    onChange={handleCardDelayInputChange}
                    onFocus={() => { isCardDelayEditingRef.current = true; }}
                    onBlur={() => {
                      isCardDelayEditingRef.current = false;
                      commitCardDelayInput();
                    }}
                    placeholder='e.g. 3.5 or [3, 5, 4]'
                    className={`video-time-input${hasRemaining ? ' input-cards-remaining' : ''}`}
                    style={{ width: '100%' }}
                  />
                  {remaining !== null && (
                    <span className={`cards-count-hint${hasRemaining ? ' cards-count-hint--remaining' : ''}`}>
                      {remaining > 0
                        ? `${remaining} ${t`cards remaining`}`
                        : remaining === 0
                          ? t`All cards covered`
                          : `${-remaining} ${t`extra entries`}`}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        ) : null}

        {/* Background Color */}
        <div className="setting-item">
          <label>{t`Background Color`}</label>
          <div className="input-wrapper">
            {settings.transparent ? (
              <div
                className="transparent-swatch"
                title="Transparent background"
              >
                T
              </div>
            ) : (
              <ColorPicker
                value={settings.background_color}
                onChange={(color) => handleSettingsChange({ background_color: color })}
              />
            )}
            <label>
              {t`Transparent`}:
              <input type="checkbox" checked={settings.transparent} onChange={handleTransparentChange} />
            </label>
          </div>
        </div>

        {/* FPS */}
        <div className="setting-item">
          <label>{t`FPS`}</label>
          <div className="input-wrapper">
            <Select
              value={settings.fps_num.toString()}
              onChange={handleFpsChange}
              className="video-setting-select video-setting-select--fps"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="24">24</MenuItem>
              <MenuItem value="30">30</MenuItem>
              <MenuItem value="60">60</MenuItem>
              <MenuItem value="90">90</MenuItem>
              <MenuItem value="120">120</MenuItem>
            </Select>
          </div>
        </div>

        {/* Resolution */}
        <div className="setting-item">
          <label className="setting-label-with-tooltip">
            {t`Resolution`}
            <div className="tooltip-wrapper">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text" style={{ paddingBottom: '1px' }}>
                {t`This setting determines the size of the video. Adjust font size accordingly.`}
              </span>
            </div>
          </label>
          <div className="input-wrapper">
            <Select
              value={settings.resolution}
              onChange={handleResolutionChange}
              className="video-setting-select video-setting-select--resolution"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="HD">HD</MenuItem>
              <MenuItem value="2K">2K</MenuItem>
              <MenuItem value="UHD">UHD</MenuItem>
              <MenuItem value="4K">4K</MenuItem>
            </Select>
          </div>
        </div>

        {/* Aspect Ratio */}
        <div className="setting-item">
          <label className="setting-label-with-tooltip">
            {t`Aspect Ratio`}
            <div className="tooltip-wrapper setting-tooltip-wrapper">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text-ratio">{t`This setting greatly affects the video size.`}</span>
            </div>
          </label>
          <div className="input-wrapper">
            <Select
              value={settings.aspect_ratio}
              onChange={handleAspectRatioChange}
              className="video-setting-select video-setting-select--aspect-ratio"
              style={{ fontSize: '16px' }}
              sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
            >
              <MenuItem value="4:3">4:3 / 1.33</MenuItem>
              <MenuItem value="16:9">16:9 / 1.78</MenuItem>
              <MenuItem value="37:20">37:20 / 1.85</MenuItem>
              <MenuItem value="2:1">2:1 / 2.00</MenuItem>
              <MenuItem value="239:100">239:100 / 2.39</MenuItem>
            </Select>
          </div>
        </div>

        {/* Video Time */}
        { settings.mode === "roller" ? (
        <div className="setting-item">
          <label>
            {timeMode === 'total' ? t`Video Time (seconds)` : t`Pixels per frame`}
          </label>
          <div className="video-time-controls">
            <input
              id="time-setting"
              type="number"
              step="1"
              value={videoTimeInput}
              onChange={handleTimeChange}
              onFocus={() => { isEditingRef.current = true; }}
              onBlur={() => {
                isEditingRef.current = false;
                const raw = videoTimeInput.trim();
                const num = parseFloat(raw);
                if (isNaN(num) || num <= 0) {
                  handleSettingsChange({ video_time: 0 });
                  setVideoTimeInput("");
                  return;
                }

                if (timeMode === 'pixels') {
                  const seconds = pixelsPerFrameToSeconds(num, settings);
                  handleSettingsChange({ video_time: seconds });
                  setVideoTimeInput(Number(num.toFixed(2)).toString());
                } else {
                  handleSettingsChange({ video_time: num });
                  setVideoTimeInput(num.toString());
                }
              }}
              className={videoTimeInput === "" ? "input-empty video-time-input" : "video-time-input"}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                value={timeMode}
                onChange={handleTimeModeChange}
                className="video-setting-select video-setting-select--time-mode"
                style={{ fontSize: '14px' }}
                sx={{
                  '.MuiSelect-select': { textAlign: 'left', paddingLeft: '8px' },
                  '.MuiSelect-icon': { color: '#007CB2' }
                }}
                MenuProps={{ PaperProps: { style: { fontSize: 13 } } }}
              >
                <MenuItem value="total">{t`Total Time`}</MenuItem>
                <MenuItem value="pixels">{t`Pixels/frame`}</MenuItem>
              </Select>
            </div>
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
};
